export type PushDebugStatus = 'received' | 'parsed' | 'displayed' | 'parse-error' | 'display-error';

export interface PushDebugEvent {
  id: string;
  receivedAt: string;
  status: PushDebugStatus;
  title?: string;
  body?: string;
  error?: string;
  displayedAt?: string;
}

const DB_NAME = 'lbc-push-debug-v1';
const STORE_NAME = 'events';

export async function readPushDebugEvents(): Promise<PushDebugEvent[]> {
  if (!('indexedDB' in window)) return [];
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = database.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll();
    request.onsuccess = () => {
      database.close();
      resolve(
        (request.result as PushDebugEvent[]).sort((left, right) =>
          right.receivedAt.localeCompare(left.receivedAt),
        ),
      );
    };
    request.onerror = () => {
      database.close();
      reject(request.error ?? new Error('Die Push-Diagnose konnte nicht gelesen werden.'));
    };
  });
}

export function subscribeToPushDebugEvents(onEvent: (event: PushDebugEvent) => void): () => void {
  const handler = (message: MessageEvent<unknown>) => {
    if (!isPushDebugMessage(message.data)) return;
    onEvent(message.data.event);
  };
  navigator.serviceWorker?.addEventListener('message', handler);
  return () => navigator.serviceWorker?.removeEventListener('message', handler);
}

function isPushDebugMessage(
  value: unknown,
): value is { type: 'lbc:push-debug'; event: PushDebugEvent } {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { type?: unknown; event?: unknown };
  return candidate.type === 'lbc:push-debug' && typeof candidate.event === 'object';
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error('Die Push-Diagnose ist nicht verfügbar.'));
  });
}
